import { lazy, ComponentType } from "react";

const RELOAD_KEY = "lovable:chunk-reloaded";
// A reload within this window of the previous one is treated as a loop: the
// error is rethrown instead so the error boundary shows. Outside the window a
// later deploy in the same tab can still recover with a fresh reload.
const RELOAD_WINDOW_MS = 10 * 60_000;
// Hard cap per tab session: a flaky network (not a stale deploy) also produces
// these errors, and a reload can't fix that, it only loses what the user typed.
const MAX_RELOADS_PER_SESSION = 3;

// Set once a reload has started. Vite's `vite:preloadError` and our own catch
// can both see the same failure; after the first one starts the reload, every
// other path must wait for it instead of rendering an error or skipping a step.
let reloading = false;

/** For tests: forget that a reload started. */
export function __resetChunkReloadForTests(): void {
  reloading = false;
}

/**
 * True for errors caused by a stale or missing hashed chunk after a deploy:
 * the old asset 404s (or the SPA fallback serves index.html in its place).
 */
export function isChunkLoadError(message: string): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Failed to load module script/i.test(message) ||
    /is not a valid JavaScript MIME type/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

/**
 * Hard-reloads the page so the browser fetches the fresh index.html and chunk
 * hashes. Returns true when a reload was started (or is already under way).
 * Guarded by sessionStorage: at most once per RELOAD_WINDOW_MS and
 * MAX_RELOADS_PER_SESSION per tab, never while offline, never without storage.
 */
export function reloadOnceForChunkError(message: string): boolean {
  if (typeof window === "undefined" || !isChunkLoadError(message)) return false;
  if (reloading) return true;
  // Offline: the chunk can't load after a reload either.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  try {
    // Value: "<last reload ms>:<reloads this session>" (legacy "1" = never).
    const [lastRaw, countRaw] = String(window.sessionStorage.getItem(RELOAD_KEY) ?? "").split(":");
    const last = Number(lastRaw) || 0;
    const count = Number(countRaw) || 0;
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    if (count >= MAX_RELOADS_PER_SESSION) return false;
    window.sessionStorage.setItem(RELOAD_KEY, `${Date.now()}:${count + 1}`);
  } catch {
    // No sessionStorage (privacy mode): don't risk a reload loop.
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

/** Never settles: the page is about to be replaced by the reload. */
const untilReload = <T,>() => new Promise<T>(() => {});

/**
 * Runs a dynamic import; on a stale-chunk failure reloads the page (returning a
 * never-settling promise while it reloads), otherwise rethrows. Also parks when
 * a reload is already under way and the import "succeeded": Vite resolves the
 * import with undefined if a `vite:preloadError` listener called
 * preventDefault(), which would otherwise crash the caller during the reload.
 */
export async function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  let mod: T;
  try {
    mod = await factory();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    if (reloadOnceForChunkError(message)) return untilReload<T>();
    throw err;
  }
  if (reloading) return untilReload<T>();
  return mod;
}

/**
 * Wraps React.lazy with automatic recovery from stale chunk errors
 * that occur after a new deploy invalidates previously-hashed chunks.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => importWithReload(factory));
}
